import { useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Gift } from 'lucide-react';
import type { StateSnapshot } from '@shared/types';
import { LOBBY_TIPS } from '@/lib/kudos';
import { api, serverNow } from '@/lib/api';
import { useGame } from '@/store/game';
import { CodeBadge } from '@/components/CodeBadge';
import { MuteToggle } from '@/components/MuteToggle';
import { ProofMark } from '@/components/ProofMark';
import { Pulse } from '@/brand/Pulse';
import { CastParade } from '@/brand/CastParade';
import { useLook } from '@/look';
import { Wordmark } from '@/brand/Wordmark';
import { Screen } from './parts/Screen';
import { TipCarousel } from './parts/TipCarousel';
import { clockLabel, startsAtLabel, timeOfDay, useSecondsUntil, useCalm, yourTime } from './parts/time';
import { HostedBy } from '@/components/HostedBy';
import { COPY } from './parts/copy';
import { useOwnSnapshot, isLobby } from './parts/frozen';
import { activePrize, prizePhrase, termsPath } from './parts/prize';
import { QUALIFY } from './parts/qualify';
import { FINISH } from './parts/finish';

const HOUR_S = 3600;
/**
 * This screen's own words (the rest come from COPY). Each thing is said once: the title, the host, the count and when it
 * starts. The heading does not echo the join button's "You're in", and the prize says how it is won, since the join card
 * has just said what it is.
 */
const LOBBY = {
  scrubbedIn: (name: string) => (name ? `Scrubbed in, ${name}.` : 'Scrubbed in.'),
  waiting: 'Starts when the host says go.',
  held: "The host has hit pause. Don't wander off.",
  screen: 'Classroom mode: questions on the big screen, answers on your phone.',
  winners: (n: number, phrase: string) => (n === 1 ? `The top player wins ${phrase}.` : `The top ${n.toLocaleString('en-GB')} each win ${phrase}.`),
  draw: (n: number) => `Plus ${n === 1 ? 'one' : n.toLocaleString('en-GB')} more in a draw you can enter from your result page.`,
};

/** M2: the lobby. Who is in, how many are with you, when it starts, and something to read while you wait. */
export default function Lobby() {
  const session = useGame((s) => s.session); const snap = useOwnSnapshot(isLobby)!; const connection = useGame((s) => s.connection);
  const challenge = useGame((s) => s.me?.challenge);
  const applySnapshot = useGame((s) => s.applySnapshot);
  const reduce = useCalm();
  const cast = useLook().look === 'medsnapp';

  // Nothing is published when a player joins, so while the room waits in the lobby refresh the count every five seconds.
  useEffect(() => {
    if (snap.status !== 'lobby' || !session) return;
    const code = session.code;
    const t = window.setInterval(() => { api<{ state: StateSnapshot }>(`/battles/${code}/state`).then((r) => applySnapshot(r.state)).catch(() => {}); }, 5000);
    return () => clearInterval(t);
  }, [snap.status, session, applySnapshot]);

  const scheduled = typeof snap.scheduledStartAt === 'number' ? snap.scheduledStartAt : undefined;
  const left = useSecondsUntil(scheduled);
  const secs = left ?? 0;
  const count = snap.playerCount ?? 1;
  const connLabel = connection === 'live' || connection === 'polling' ? COPY.lobby.connected : COPY.lobby.connecting;
  const prize = activePrize(snap.prize);
  const code = session?.code ?? snap.code;

  return (
    <Screen phase="lobby" label={snap.title} header={(
      // The lobby's header carries the sound toggle with its words ("Sound on"), so nobody misses the countdown's heartbeat.
      // At 320 px wide the wordmark eases down a little so the chip keeps its words on one line. Beside it, the person
      // icon (9 Oct) while the address has played before and is not yet proved: it draws itself only then.
      <header className="flex items-center justify-between gap-3">
        <Wordmark variant="compact" className="min-w-0 text-[clamp(0.9rem,4.5vw,1.0625rem)]" />
        <div className="flex shrink-0 items-center gap-1.5 text-ink-2">
          <ProofMark />
          <MuteToggle variant="chip" className="shrink-0 whitespace-nowrap" />
        </div>
      </header>
    )}>
      <header className="flex flex-col gap-1 pt-2">
        <p className="font-semibold text-ink-2">{snap.title}</p>
        <h1 className="display text-[2.75rem] text-ink">{LOBBY.scrubbedIn(session?.name ?? '')}</h1>
        {snap.hostName && <HostedBy host={snap.hostName} className="text-ink-2" />}
        {snap.qualify && <p data-part="qualify-rule" className="font-semibold text-brand-strong">{QUALIFY.rule(snap.qualify)}</p>}
        {/* 14 Oct: came through a friend's link: the score to beat. */}
        {challenge && <p data-part="challenge" className="rounded-tile bg-cart-soft px-4 py-3 font-semibold text-ink">{FINISH.challengeLobby(challenge.name, challenge.score, challenge.title)}</p>}
      </header>

      <section aria-live="polite" className="relative overflow-hidden rounded-card bg-surface px-5 pb-4 pt-5 shadow-card">
        {/* The count scales with the phone (it never pushes its label off a 320 px screen); a long count puts the label below. */}
        <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span key={count} className="numeral block text-[clamp(3rem,20vw,5.5rem)] leading-[0.85] text-ink"
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18, scale: 1.12 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={reduce ? { opacity: 0 } : { opacity: 0, y: -18 }}
              transition={{ type: 'spring', stiffness: 520, damping: 26 }}>
              {count.toLocaleString('en-GB')}
            </motion.span>
          </AnimatePresence>
          <span className="min-w-[4.5rem] pb-1.5 font-semibold leading-tight text-ink-2">{COPY.lobby.players(count)}</span>
        </div>
        {cast
          ? <CastParade size={56} className="mt-3" />
          : <div className="mt-3 text-brand"><Pulse mood="steady" width="100%" height={36} /></div>}
      </section>

      {prize && (
        // Who wins what, any draw, and the terms one tap away (they open in a new tab so the lobby keeps its place).
        <motion.section aria-label="Prize" initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 360, damping: 28, delay: 0.15 }}
          className="flex items-start gap-3 rounded-card bg-lime-soft px-4 py-3.5 shadow-card">
          <span aria-hidden="true" className="grid size-10 shrink-0 place-items-center rounded-tile bg-lime text-ink"><Gift className="size-5" strokeWidth={2.4} /></span>
          <div className="min-w-0">
            <p className="font-display text-lg font-extrabold leading-snug text-ink">{LOBBY.winners(prize.winners, prizePhrase(prize.prize))}</p>
            <p className="mt-0.5 text-sm leading-snug text-ink-2">
              {prize.drawPrizes > 0 && <>{LOBBY.draw(prize.drawPrizes)}{' '}</>}
              <a href={termsPath(code)} target="_blank" rel="noopener" className="py-3 font-semibold text-iv-strong underline underline-offset-2">{COPY.prize.termsApply}</a>
            </p>
          </div>
        </motion.section>
      )}

      {scheduled !== undefined && (
        // The start, said once: the time of day while it is an hour or more away, then the countdown.
        <section className="flex items-center gap-4 rounded-card bg-ink px-5 py-4 text-surface">
          {snap.hold ? (
            <p className="font-semibold">{LOBBY.held}</p>
          ) : secs >= HOUR_S ? (
            <div>
              <p className="text-sm font-semibold text-surface/75">{startsAtLabel(scheduled, serverNow())}</p>
              <p className="flex items-baseline gap-2"><span className="numeral text-5xl leading-none text-lime">{timeOfDay(scheduled)}</span><span className="text-sm font-semibold text-surface/75">{yourTime(scheduled)}</span></p>
            </div>
          ) : secs > 0 ? (
            <div>
              <p className="text-sm font-semibold text-surface/75">{COPY.lobby.startsIn}</p>
              {/* The last ten seconds drop in one by one, like the question clock. */}
              <p key={secs <= 10 ? `s${secs}` : 'clock'} className={`numeral text-5xl leading-none ${secs <= 10 ? 'rd-drop' : ''}`} aria-live="off">{clockLabel(secs)}</p>
            </div>
          ) : (
            <p className="font-semibold">{COPY.lobby.startingNow}</p>
          )}
        </section>
      )}
      {scheduled === undefined && <p className="text-ink-2">{snap.hold ? LOBBY.held : LOBBY.waiting}</p>}
      {snap.display === 'screen' && <p className="rounded-tile bg-iv-soft px-4 py-3 text-sm font-semibold text-iv-strong">{LOBBY.screen}</p>}

      <TipCarousel tips={LOBBY_TIPS} />

      <footer className="mt-auto flex items-center justify-between gap-3 pt-2 text-xs text-ink-3">
        <CodeBadge code={session?.code ?? snap.code} />
        <span>{connLabel}</span>
      </footer>
    </Screen>
  );
}
